import React from "react";
import * as Select from "@radix-ui/react-select";
import { useField } from "formik";
import { Check, ChevronDown } from "lucide-react";
import { Displayable, WithId } from "django-rest-react";
import { FieldWrapper } from "./FieldWrapper";
import clsx from "clsx";
import { InputDefaults, inputClasses } from "./form-defaults";

export type GoodMap<T> = (val: T) => string;
export type Value = string | number;
export type RestoreMap = (internal: string) => Value;

const CLEAR_VALUE = "__CLEAR__";

export const getMappings: <T extends WithId>(
  choices: T[],
  displayChoices: GoodMap<T> | undefined,
) => [GoodMap<T>, GoodMap<T>, RestoreMap] = (choices, displayChoices) => {
  // Used only if choices is empty
  const identity = <T,>(val: T) => val as unknown as string;

  const getId = <T extends WithId>(val: T) => val.id.toString();
  const displayDisplayable = <T,>(val: T) => (val as Displayable).display;

  if (choices.length == 0) {
    return [displayChoices || identity, identity, identity];
  }
  const restore =
    typeof choices[0].id === "number"
      ? (internal: string) => (internal === "" ? "" : parseInt(internal))
      : identity;
  return [displayChoices || displayDisplayable, getId, restore];
};

const FormikCustomSelect = <T extends WithId>({
  name,
  choices,
  displayChoice,
  helpText,
  label,
  onChange,
  required = false,
  disabled = false,
  readOnly = false,
  wrapped = true,
}: {
  choices: T[];
  displayChoice?: (val: T) => string;
  required?: boolean;
} & InputDefaults<string>) => {
  const [field, meta, helpers] = useField<Value>(name);
  const [display, getOpt, restore] = getMappings(choices, displayChoice);

  const internal = (
    <div>
      <Select.Root
        value={field.value?.toString()}
        onValueChange={val => {
          let newval = val === CLEAR_VALUE ? "" : val;
          if (!!onChange) {
            newval = onChange(newval);
          }
          helpers.setValue(restore(newval));
        }}
        disabled={readOnly}
      >
        <Select.Trigger
          className={clsx(
            inputClasses(!!readOnly || !!disabled, !!meta.error),
            "inline-flex items-center justify-between focus:outline-none",
          )}
        >
          <Select.Value />
          <Select.Icon>
            <ChevronDown className="ml-2 h-4 w-4" />
          </Select.Icon>
        </Select.Trigger>
        <Select.Portal>
          <Select.Content className="rounded-xl bg-background shadow-lg border mt-1 z-50">
            <Select.Viewport className="p-2">
              {!required && (
                <Select.Item
                  value={CLEAR_VALUE}
                  key={-2}
                  className="relative flex items-center px-3 py-4 rounded-md text-sm cursor-pointer hover:bg-primary select-none"
                >
                  <Select.ItemText></Select.ItemText>
                  <Select.ItemIndicator className="absolute right-2">
                    <Check className="h-4 w-4 text-primary" />
                  </Select.ItemIndicator>
                </Select.Item>
              )}
              {choices.map(opt => (
                <Select.Item
                  key={getOpt(opt)}
                  value={getOpt(opt)}
                  className={clsx(
                    "relative flex items-center px-3 py-2 rounded-md text-sm cursor-pointer select-none outline-none",
                    "data-[highlighted]:bg-primary data-[highlighted]:text-on-accent",
                  )}
                >
                  <Select.ItemText>{display(opt)}</Select.ItemText>
                  <Select.ItemIndicator className="absolute right-2">
                    <Check className="h-4 w-4 text-primary" />
                  </Select.ItemIndicator>
                </Select.Item>
              ))}
            </Select.Viewport>
          </Select.Content>
        </Select.Portal>
      </Select.Root>
    </div>
  );

  return (
    <FieldWrapper
      name={name}
      label={label}
      helpText={helpText}
      error={meta.touched && meta.error ? meta.error : undefined}
      wrapped={wrapped}
    >
      {internal}
    </FieldWrapper>
  );
};

export default FormikCustomSelect;
